/**
 * Keeps secret-bearing URLs out of everything this app sends to Bayawatch.
 *
 * Four routes carry a bearer secret in the PATH (`routes/routes.tsx`):
 * `/reset_password/:token`, `/public/issues/:token`, `/public/reports/:token`
 * and `/public/reports/:token/issues/:issueId`. Whoever holds the token can
 * reset the password or read the shared issue/report, so the token must not
 * reach a third system — and `@bayawatch/sdk` stamps `window.location.href` on
 * every error event and every session-replay batch. Since SDK 0.4.0 `redactUrl`
 * is installed as its `sanitizeUrl` hook (`bayawatch.ts`), which the SDK
 * applies to each of those URLs and to the URL-bearing attributes rrweb
 * records; `redactBayawatchEvent` still scrubs error TEXT and the error's
 * `context` (where the SDK copies a failed request's path), which the hook
 * does not reach.
 *
 * KEEP IN SYNC with `redactUrl` in `scripts/engineFloorGate.js`, an ES5 copy
 * (that file is inlined into index.html before any module loads, so it cannot
 * import this one). `bayawatchPrivacy.test.ts` runs both implementations over
 * the same cases, so a drift between them fails the suite.
 */

import type { createBrowserRouter } from 'react-router-dom'
import { isReplaySafePage, isReplayStarted } from '@/lib/monitoring/sessionReplayGate'

// react-router matches paths case-insensitively and decodes every path segment
// before matching (`decodePath` in @remix-run/router), so `/Public/%72eports/<t>`
// renders the shared report exactly like `/public/reports/<t>` — the match runs
// on the decoded text with the `i` flag. Repeated slashes are accepted as a
// harmless superset. The match is not anchored, so `/api/...` spellings inside
// error messages are covered too. In a URL only the token segment is replaced
// (`/issues/42` after it is kept); in free text there is no reliable end of the
// URL, so everything after the token up to whitespace or a quote goes with it.
//
// Signup (docs/plans/rejestracja-z-linku.md, section 6): the segment after
// `/signup` on page routes (`/signup/<code>`, and `/signup/confirm`, whose
// token sits in the fragment — in free text that fragment goes with it) and
// after `/signup/links` on API paths (`/signup/links/<code>/requests`). The
// campaign code grants no data, but it lets anyone send the form, so it stays
// out of telemetry too. `/signup/links` and `/signup/confirmations` are the
// static API segments and are kept.
const SECRET_PATH =
  /(\/+(?:reset_password|public\/+issues|public\/+reports|signup(?:\/+links|(?!\/+(?:links|confirmations)(?:[/?#\s"'<>`]|$)))))\/+[^/?#\s]+/i
const SECRET_IN_PATH =
  /(\/+(?:reset_password|public\/+issues|public\/+reports|signup(?:\/+links|(?!\/+(?:links|confirmations)(?:[/?#\s"'<>`]|$)))))\/+[^/?#\s]+/gi
// Skips a token segment that is already `:token`: `filterBayaWatchPayload`
// (lib/monitoring/httpFailureReporting.ts) runs first and redacts the current
// page's secret by value, keeping the rest of the URL (`/issues/5`) readable.
const SECRET_IN_TEXT =
  /(\/+(?:reset_password|public\/+issues|public\/+reports|signup(?:\/+links|(?!\/+(?:links|confirmations)(?:[/?#\s"'<>`]|$)))))\/+(?!:token(?:[/?#]|$))[^\s"'<>`]+/gi

export const REDACTED = ':token'

/** `decodeURI` keeps `%2F` encoded, as the router does; malformed input stays raw. */
function decodeForMatching(text: string): string {
  try {
    return decodeURI(text)
  } catch {
    return text
  }
}

function replaceSecrets(text: string, pattern: RegExp): string {
  const decoded = decodeForMatching(text)
  if (!SECRET_PATH.test(decoded)) return text
  const redacted = decoded.replace(pattern, `$1/${REDACTED}`)
  return redacted === decoded ? text : redacted
}

// A whitespace/quote-delimited run: the unit free text is decoded in, so text
// around a secret path (other stack frames, `my%20chunk.js`) stays byte-exact.
const TEXT_RUN = /[^\s"'<>`]+/g

/**
 * Replace every secret path — and whatever URL text follows it — inside free
 * text (messages, stacks). Text without a secret path is returned unchanged.
 */
export function redactSecretPaths(text: string): string {
  return text.replace(TEXT_RUN, run => replaceSecrets(run, SECRET_IN_TEXT))
}

/**
 * True when the path carries a secret, i.e. the page must not be recorded.
 * Fails CLOSED: a location that is not a string counts as secret.
 */
export function isSecretBearingPath(pathname: unknown): boolean {
  if (typeof pathname !== 'string') return true
  return SECRET_PATH.test(decodeForMatching(pathname))
}

/**
 * Redact a URL for telemetry: secret path segments become `:token`; on a secret
 * page the whole query becomes `?redacted`, elsewhere query VALUES become
 * `redacted` (keys are kept, they help diagnose things like the `?_reload`
 * loop guard); a fragment always collapses to `#redacted`.
 */
export function redactUrl(href: string): string {
  let rest = String(href)
  let fragment = ''
  const hashAt = rest.indexOf('#')
  if (hashAt !== -1) {
    fragment = '#redacted'
    rest = rest.slice(0, hashAt)
  }
  let rawQuery: string | null = null
  const queryAt = rest.indexOf('?')
  if (queryAt !== -1) {
    rawQuery = rest.slice(queryAt + 1)
    rest = rest.slice(0, queryAt)
  }
  let query = ''
  if (rawQuery !== null && isSecretBearingPath(rest)) {
    query = '?redacted'
  } else if (rawQuery !== null) {
    const keys = rawQuery
      .split('&')
      .filter(pair => pair !== '')
      .map(pair => {
        const eq = pair.indexOf('=')
        return eq === -1 ? pair : `${pair.slice(0, eq)}=redacted`
      })
    query = keys.length ? `?${keys.join('&')}` : ''
  }
  return replaceSecrets(rest, SECRET_IN_PATH) + query + fragment
}

type BayawatchEventLike = {
  url?: unknown
  message?: unknown
  stack?: unknown
  context?: unknown
}

/** Redacts every property name and string in plain JSON data. */
function redactJsonData(value: unknown): unknown {
  if (typeof value === 'string') return redactSecretPaths(value)
  if (Array.isArray(value)) return value.map(redactJsonData)
  if (value === null || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [redactSecretPaths(key), redactJsonData(item)]),
  )
}

/**
 * Redacts `context` as the SDK will send it. A JSON round trip first turns it
 * into plain data — `toJSON` output (`Date`, `URL`) included, nothing the
 * caller passed in edited — and the redaction then runs on its property names
 * and strings one by one, never on JSON text, so a decoded `%22` cannot
 * become syntax. Same guarantee as `message` and `stack`: a secret PATH is
 * redacted; a token copied into text without its path is not recognisable,
 * there or here. A context that does not serialize (a cycle) is dropped, not
 * sent raw.
 */
function redactContext(context: unknown): unknown {
  try {
    const json = JSON.stringify(context)
    return json === undefined ? undefined : redactJsonData(JSON.parse(json))
  } catch {
    return undefined
  }
}

/**
 * The SDK's `beforeSend`: scrub the event in place and return it. The SDK
 * merges the return value back with `Object.assign`, so every field that can
 * carry the page URL is rewritten here — `url` always, `message` and `stack`
 * because an error text can quote a request path (`/api/public/reports/<t>`),
 * and `context` as serialized, where the SDK copies the failing request's
 * path from an axios-shaped error (`context.request.url`, query and fragment
 * stripped but the path kept).
 */
export function redactBayawatchEvent<T extends BayawatchEventLike>(event: T): T {
  // Context first: serializing it runs caller `toJSON`s, which must not get
  // to write a fresh secret into a field already redacted.
  if (event.context !== undefined) event.context = redactContext(event.context)
  if (typeof event.url === 'string') event.url = redactUrl(event.url)
  if (typeof event.message === 'string') event.message = redactSecretPaths(event.message)
  if (typeof event.stack === 'string') event.stack = redactSecretPaths(event.stack)
  return event
}

/**
 * Call BEFORE an in-app navigation whose target is not known to be ordinary —
 * today the native push / universal-link deep links (`lib/deepLinkNavigation.ts`).
 * `bayawatch.ts` decides once, when the SDK loads, whether to record; a session
 * that started on an ordinary page would keep recording after the SPA moved to
 * a token route. Since SDK 0.4.0 `sanitizeUrl` keeps the token out of every URL
 * that recording would send, but not out of text the token route renders, so
 * the same defence in depth as the load-time gate applies: when the target is
 * secret-bearing the SDK is torn down before navigating. `destroy()` flushes
 * the buffered replay and errors (payloads are built at flush time, from the
 * old URL), removes every listener and — since 0.4.0 — cancels a recording
 * start still queued behind the `/sdk-config` preflight, so nothing new is
 * captured afterwards. Monitoring then stays off until the next app launch — a
 * deliberate trade for a rare path (opening a shared link inside the logged-in
 * native app).
 *
 * "Secret" uses the same deny-by-default rule as the load-time replay gate
 * (`isReplaySafePage`), so a target that gate would refuse to record is never
 * recorded by navigating into it either.
 */
export function stopRecordingBeforeSecretNavigation(path: string): void {
  const target = typeof path === 'string' ? `https://placeholder.invalid${path}` : ''
  if (!isSecretBearingPath(path) && isReplaySafePage(target)) return
  try {
    const instance = (window as unknown as { __bayawatch_instance__?: { destroy: () => void } })
      .__bayawatch_instance__
    instance?.destroy()
  } catch {
    // Monitoring is best-effort and must never block navigation.
  }
}

/**
 * Runs the guard above on every router transition, not only on native deep
 * links: a recorded page that navigates in-app into a page the load-time gate
 * refuses — the signup section, whose privacy policy promises it is never
 * recorded, or a token route — stops the SDK before the destination renders.
 * The router notifies its subscribers before React re-renders, and this
 * subscription is installed before `RouterProvider` adds its own.
 *
 * Only while a recording is running: public token pages link to each other,
 * were never recorded, and keep error capture when the user moves between
 * them.
 */
export function guardRecordingAcrossNavigations(
  router: Pick<ReturnType<typeof createBrowserRouter>, 'subscribe'>,
): void {
  router.subscribe(state => {
    if (!isReplayStarted()) return
    const target = state.navigation.location ?? state.location
    stopRecordingBeforeSecretNavigation(`${target.pathname}${target.search}${target.hash}`)
  })
}
